import React, { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { getFormSubmitErrors, getFormValues } from 'redux-form';
import { withRouter } from 'react-router';
import { Grid } from 'semantic-ui-react';
import { omit } from 'lodash';

import * as actions from '../state/calendar/actions';
import { submitForm, destroyForm } from 'State/forms/actions';

import { Filter, Main } from 'Containers';
import SchoolCalendarForm from '../components/SchoolCalendarForm';
import { ExceptionTable } from '../components/ExceptionTable';
import { getIsDirty } from 'Common/Selectors/DirtySelector';
import {
  SCHOOL_CALENDAR_FORM_NAME,
  SCHOOL_CALENDAR_EXCEPTION_FORM_NAME,
} from 'Common/FormEnum';
import { errorToast, createToast, toastType } from 'Common/ToastHelper';
import { SchoolCalendarFieldEnum as Fields } from 'Common/FieldEnum';
import { ExceptionModal } from '../components/ExceptionModal';
import { LoadingPage, DirtyWrapper, YesNoDialog } from 'Components';
import { AssignmentTable } from '../components/AssignmentTable';
import { exceptionsHaveChanged } from '../helpers';
import { TitledCard } from 'Components';

const newException = {
  [Fields.Description]: '',
  [Fields.IsServing]: false,
  [Fields.BeginDay]: null,
  [Fields.EndDay]: null,
};

class SchoolCalendarEdit extends Component {
  static path = '/schoolcalendar/:id';

  state = {
    exceptionModal: {
      exception: { ...newException },
      isOpen: false,
      isAdd: false,
      index: null,
    },
    mbeWarningDialog: {
      isOpen: false,
    },
  };

  componentDidMount() {
    const { id } = this.props.match.params;
    this.props.fetchCalendar(id);
  }

  componentDidUpdate(prevProps) {
    const { clearSchoolCalendarErrorSuccess } = this.props;

    if (errorToast(this.props, prevProps, 'submitError')) {
      clearSchoolCalendarErrorSuccess();
    } else if (errorToast(this.props, prevProps, 'fetchError')) {
      clearSchoolCalendarErrorSuccess();
    } else if (this.props.submitted && !prevProps.submitted) {
      createToast(
        toastType.success,
        'Success',
        'School Calendar saved successfully',
        true
      );

      clearSchoolCalendarErrorSuccess();
    }
  }

  resetScroll = () => this.props.scrollMainTo(0, 0);

  completeSave = () => {
    this.props.submitForm(SCHOOL_CALENDAR_FORM_NAME);
  };

  closeMbeWarningDialog = () => {
    this.setState({ mbeWarningDialog: { isOpen: false } });
  };

  onSaveClick = () => {
    const { initialData, assignments } = this.props;

    if (initialData[Fields.MbeComplete]) {
      const mbeWarningSites = assignments.filter(
        a => a[Fields.Assigned] && !a[Fields.MbeComplete]
      );

      if (mbeWarningSites.length > 0) {
        this.setState({ mbeWarningDialog: { isOpen: true } });
        return;
      }
    }

    this.completeSave();
  };

  handleMainSubmit = model => {
    this.closeMbeWarningDialog();
    return this.props.saveCalendar(model, this.resetScroll);
  };

  onAddUpdateExceptionClick = () => {
    this.props.submitForm(SCHOOL_CALENDAR_EXCEPTION_FORM_NAME);
  };

  handleExceptionSubmit = exception => {
    const { index } = this.state.exceptionModal;

    if (index !== null) {
      this.props.editException(exception, index);
    } else {
      this.props.addException(exception);
    }

    this.closeExceptionModal();
  };

  handleExceptionRemoval = index => {
    this.props.removeException(index);
  };

  onEditExceptionClick = (exception, index) => {
    this.setState({
      exceptionModal: {
        isOpen: true,
        isAdd: false,
        exception,
        index,
      },
    });
  };

  showAddExceptionModal = e => {
    e.preventDefault();
    this.setState({
      exceptionModal: {
        isOpen: true,
        isAdd: true,
        exception: { ...newException },
        index: null,
      },
    });
  };

  closeExceptionModal = () => {
    this.setState({
      exceptionModal: {
        isOpen: false,
        isAdd: false,
      },
    });
  };

  getRootUrl = () => {
    const { url } = this.props.match;
    return url.substring(0, url.lastIndexOf('/'));
  };

  getMainProps = () => {
    const { submitting, isReadOnly, formHasErrors } = this.props;

    return {
      toolbox: {
        backButtonProps: { shown: true, path: this.getRootUrl() },
        rightButtons: [
          {
            content: 'Save',
            disabled: submitting || isReadOnly || formHasErrors,
            loading: submitting,
            onClick: this.onSaveClick,
          },
        ],
        shown: true,
      },
    };
  };

  render() {
    const {
      assignments,
      calendar,
      exceptions,
      fetching,
      firstDay,
      initialData,
      isReadOnly,
      lastDay,
      toggleAssignment,
      toggleCheckAllAssignments,
    } = this.props;

    const {
      exceptionModal: { isAdd, isOpen, exception },
      mbeWarningDialog: { isOpen: dialogOpen },
    } = this.state;

    const { id } = this.props.match.params;
    const title = `School Calendar - ${id === '0' ? 'Add' : 'Edit'}`;

    return (
      <Fragment>
        {fetching && <LoadingPage />}
        <Filter title={title} />
        <Main {...this.getMainProps()}>
          <DirtyWrapper
            closeModalYesCallback={this.closeExceptionModal}
            isReadOnly={isReadOnly}
            watchedForm={SCHOOL_CALENDAR_EXCEPTION_FORM_NAME}
          >
            {showDirtyConfirm =>
              isOpen && (
                <ExceptionModal
                  exception={exception}
                  firstDay={firstDay}
                  isAdd={isAdd}
                  lastDay={lastDay}
                  onClose={showDirtyConfirm}
                  onActionClick={this.onAddUpdateExceptionClick}
                  onSubmit={this.handleExceptionSubmit}
                />
              )
            }
          </DirtyWrapper>
          <YesNoDialog
            showDialog={dialogOpen}
            title={'Meal Benefit Expiration Warning'}
            question={
              'The Meal Benefit period has already expired for this calendar.  Students without an application at the assigned sites will be set to full pay.  Continue?'
            }
            yesCallback={this.completeSave}
            noCallback={this.closeMbeWarningDialog}
          />
          <Grid>
            <Grid.Column>
              <Grid.Row>
                <TitledCard title={'Calendar Information'}>
                  <SchoolCalendarForm
                    exceptions={exceptions}
                    initialValues={{
                      ...omit(calendar, [
                        Fields.SchoolCalendarExceptions,
                        Fields.SchoolCalendarSiteMasters,
                      ]),
                      Id: id,
                    }}
                    isReadOnly={isReadOnly}
                    onSubmit={this.handleMainSubmit}
                  />
                </TitledCard>
              </Grid.Row>
              <Grid.Row>
                <label className={'text-orange'}>Exceptions</label>
              </Grid.Row>
              <Grid.Row>
                <ExceptionTable
                  exceptions={exceptions}
                  isReadOnly={isReadOnly}
                  firstDay={firstDay}
                  lastDay={lastDay}
                  loading={fetching}
                  onAddClick={this.showAddExceptionModal}
                  onDelete={this.handleExceptionRemoval}
                  onEdit={this.onEditExceptionClick}
                />
              </Grid.Row>
              <Grid.Row>
                <label className={'text-orange'}>Assign to Sites</label>
              </Grid.Row>
              <Grid.Row>
                <AssignmentTable
                  assignments={assignments}
                  initialData={initialData[Fields.SchoolCalendarSiteMasters]}
                  isReadOnly={isReadOnly}
                  loading={fetching}
                  mbeComplete={calendar[Fields.MbeComplete]}
                  onToggle={toggleAssignment}
                  onToggleAll={toggleCheckAllAssignments}
                />
              </Grid.Row>
            </Grid.Column>
          </Grid>
        </Main>
      </Fragment>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  const formErrors = getFormSubmitErrors(SCHOOL_CALENDAR_FORM_NAME)(state);
  const formValues = getFormValues(SCHOOL_CALENDAR_FORM_NAME)(state);
  const { calendar } = state.SchoolCalendarState;

  const changed = exceptionsHaveChanged(
    calendar.data[Fields.SchoolCalendarExceptions],
    calendar.initialData[Fields.SchoolCalendarExceptions]
  );

  ownProps.setPageLevelDirty(
    changed || getIsDirty(SCHOOL_CALENDAR_FORM_NAME)(state)
  );

  return {
    calendar: calendar.data,
    initialData: calendar.initialData,
    submitted: calendar.submitted,
    submitError: calendar.submitError,
    fetchError: calendar.fetchError,
    exceptions: calendar.data[Fields.SchoolCalendarExceptions],
    initialExceptions: calendar.initialData[Fields.SchoolCalendarExceptions],
    fetching: calendar.fetching,
    submitting: calendar.submitting,
    formHasErrors: formErrors ? Object.keys(formErrors).length > 0 : false,
    firstDay: formValues ? formValues[Fields.FirstDayDT] : '',
    lastDay: formValues ? formValues[Fields.LastDayDT] : '',
    assignments: calendar.data[Fields.SchoolCalendarSiteMasters],
  };
};

SchoolCalendarEdit = withRouter(
  connect(
    mapStateToProps,
    {
      ...actions,
      submitForm,
      destroyForm,
    }
  )(SchoolCalendarEdit)
);

export { SchoolCalendarEdit };



// WEBPACK FOOTER //
// ./src/pages/schoolcalendar/subpages/SchoolCalendarEdit.js